import styled from '@emotion/styled'
import { type ReactNode, useEffect, useId } from 'react'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  /** Optional footer (action buttons). */
  footer?: ReactNode
}

// Modal is the shared accessible dialog (role=dialog, aria-modal) used for
// confirmations and editor panels (KAT-297). It closes on Escape and on a
// backdrop click, and locks body scroll while open.
export function Modal({ open, onClose, title, children, footer }: ModalProps) {
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, onClose])

  if (!open) return null

  return (
    <Backdrop onClick={onClose}>
      <Panel role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title id={titleId}>{title}</Title>
          <Close type="button" aria-label="Close" onClick={onClose}>
            ✕
          </Close>
        </Header>
        <Body>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </Backdrop>
  )
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 20, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--s-5);
  z-index: 1000;
  animation: modal-fade var(--d-fast) var(--ease-out);

  @keyframes modal-fade {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Panel = styled.div`
  background: var(--c-bg-elev);
  border: 1px solid var(--c-border);
  border-radius: var(--r-lg);
  box-shadow: var(--e-4);
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--s-4) var(--s-5);
  border-bottom: 1px solid var(--c-border);
`

const Title = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
`

const Close = styled.button`
  background: transparent;
  border: none;
  color: var(--c-fg-subtle);
  font-size: 15px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--r-sm);

  &:hover {
    color: var(--c-fg);
    background: var(--c-bg-sunk);
  }
  &:focus-visible {
    outline: 2px solid var(--c-accent);
    outline-offset: 2px;
  }
`

const Body = styled.div`
  padding: var(--s-5);
  overflow-y: auto;
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--s-3);
  padding: var(--s-4) var(--s-5);
  border-top: 1px solid var(--c-border);
`
